/* eslint-disable react/display-name */
import React, { memo, useState } from 'react';
import styled from 'styled-components';

import { smallMediaQuery, mediumMediaQuery } from '../../common/utilities/sizeHelpers';
import { getQueryParameterByName, updateQueryStringParameter } from '../../common/utilities/queryParameters';
import {
    Map,
    RasterLayer,
    MapControlContainer,
    MapLegend,
    MapZoomPicker,
    Icons
} from '@weatherbug/react';

import '../../../../node_modules/mapbox-gl/dist/mapbox-gl.css';
import { BotMap } from '../common/botMapView/botMapView';
import { translate } from '../../common/translation/translate';

const { CloseIcon } = Icons;
const defaultLayerId = 'dta:nws';
const defaultZoom = 6;
const layerOpacity = 70;
const showOverlays = true;
const enableLegend = true;
const showLegendToggleButton = false;
const showMapControlActionButton = false;

interface AlertsMapProps {
    layerId: string;
    latitude: number;
    longitude: number;
    mapboxAccessToken: string;    
    mapBaseUrl: string;
    tileBaseUrl: string;
    tempUnit: number;
    pressureUnit: number;
    rainUnit: number;
    windUnit: number;
    showAttributionIcon?: boolean;
    authId: string;
    secret: string;
    isAirQualityPage?: string;
    stdParameters: Record<string, unknown>;
    airQualityAttributionUrl: string;
    isBot?: boolean;
    pageName?: string;
}

export const AlertsMap = memo(({
    layerId,
    latitude,
    longitude,
    mapboxAccessToken,    
    mapBaseUrl,
    tileBaseUrl,
    tempUnit,
    pressureUnit,
    rainUnit,
    windUnit,
    showAttributionIcon,
    authId,
    secret,
    stdParameters,
    isAirQualityPage,
    airQualityAttributionUrl,
    isBot,
    pageName=''
}: AlertsMapProps) => {
    const [enableZoomPicker, setEnableZoomPicker] = useState(false);
    if (!layerId) {
        layerId = defaultLayerId;
    }

    const onPanned = (latitude: number, longitude: number) => {
        setCenterUrlParam(latitude, longitude, undefined);
    };

    const onZoomChanged = (zoom: any) => {
        setCenterUrlParam(undefined, undefined, zoom);
    };

    const setCenterUrlParam = (newLatitude: any, newLongitude: any, newZoom: any) => {
        const center = getQueryParameterByName('center');
        let latitude = newLatitude;
        let longitude = newLongitude;
        let zoom = newZoom;
  
        if (center) {
            const centerArr = center.split(',');
            latitude = latitude || centerArr[0];
            longitude = longitude || centerArr[1];
            zoom = zoom || centerArr[2];
        } else {
            latitude = latitude;
            longitude = longitude;
            zoom = zoom || defaultZoom;
        }
  
        if(latitude && longitude && zoom) {
            const newCenter = `${latitude},${longitude},${zoom}`;
            const newUrl = updateQueryStringParameter(window.location.href, 'center', newCenter);
            window.history.replaceState('', '', newUrl);
        }
    };

    if (!latitude || !longitude || !mapboxAccessToken) {
        return <Container/>;
    }

    return (
        <Container isBot={isBot} isAirQualityPage={!!isAirQualityPage} id='com.weatherbug.web:id/alertsMap'>
            {
                isBot 
                    ? <BotMap mapContentText={`${pageName} ${translate('map')}`} />
                    : <Map
                        mapboxAccessToken={mapboxAccessToken}
                        initialLongitude={longitude}
                        initialLatitude={latitude}
                        initialZoom={defaultZoom}
                        longitude={longitude}
                        latitude={latitude}
                        enableDragRotate={true}
                        enablePan={true}
                        enableZoom={true}
                        showFullscreenControl={false}
                        showNavigationControl={false}
                        showCenterMarker={true}
                        centerMarkerLatitude={latitude}
                        centerMarkerLongitude={longitude}
                        smallPinImageUrl='img/map-pin-active.png'
                        bigPinImageUrl='img/map-pin-active.png'
                        onPanned={onPanned}
                        onZoomChanged={onZoomChanged}
                        extension={(
                            map: () => void, 
                            latitude: number, 
                            longitude: number, 
                            mapZoomOutPressed: () => void, 
                            mapZoomInPressed: () => void, 
                            mapPanToMarkerPressed: () => void, 
                            currentZoomLevel: number, 
                            changeZoom: number
                        ) => {
                            return map && <RasterLayer
                                map={map}
                                airQualityAttributionUrl={airQualityAttributionUrl}
                                latitude={latitude}
                                longitude={longitude}
                                defaultLayerId={layerId}
                                mapBaseUrl={mapBaseUrl}
                                tileBaseUrl={tileBaseUrl}
                                authId={authId}
                                secret={secret}
                                stdApiParameters={stdParameters}
                                layerOpacity={layerOpacity}
                                tempUnit={tempUnit}
                                pressureUnit={pressureUnit}
                                rainUnit={rainUnit}
                                windUnit={windUnit}
                                showAttributionIcon={showAttributionIcon}
                                controlContainer={(
                                    layers: [],
                                    onLayerPressed: () => void,
                                    currentLayerId: number,
                                    animationEnabled: boolean,
                                    animationTime: string,
                                    animationCallback: () => void,
                                    progressPercentage: number,
                                    legendColors: [],
                                    legendLabels: [],
                                    alertsLegend: any,
                                    legendSets: [],
                                    legendUnits: []
                                ) => {
                                    return (
                                        showOverlays && <div>
                                            <MapControlContainer
                                                layers={layers}
                                                onLayerPressed={onLayerPressed}
                                                currentLayerId={layerId}
                                                legendColors={legendColors}
                                                legendLabels={legendLabels}
                                                alertsLegend={alertsLegend}
                                                legendSets={legendSets}
                                                mapZoomOutPressed={mapZoomOutPressed}
                                                mapZoomInPressed={mapZoomInPressed}
                                                mapPanToMarkerPressed={mapPanToMarkerPressed}
                                                currentZoomLevel={currentZoomLevel}
                                                changeZoom={changeZoom}
                                                enableZoomPicker={enableZoomPicker}
                                                onToggleZoomPicker={setEnableZoomPicker}
                                                zoomInButtonEnabled={true}
                                                zoomOutButtonEnabled={true}
                                                enableLegend={enableLegend}
                                                showMapControlActionButton={showMapControlActionButton}
                                                showLegendToggleButton={showLegendToggleButton}
                                                legendUnits={legendUnits}
                                                legend={(
                                                    legendColors: [],
                                                    legendLabels: [],
                                                    alertsLegend: any,
                                                    legendSets: [],
                                                    legendUnits: []
                                                ) => {
                                                    return (
                                                        <MapLegend
                                                            legendColors={legendColors}
                                                            legendLabels={legendLabels}
                                                            alertsLegend={alertsLegend}
                                                            legendSets={legendSets}
                                                            legendUnits={legendUnits}
                                                        />
                                                    );
                                                }}                                        
                                                zoomPicker={(
                                                    currentZoomLevel: number,
                                                    onZoomChanged: () => void
                                                ) => {
                                                    return (
                                                        <MapZoomPicker
                                                            headerImage={<CloseIcon />}
                                                            onClickHeaderImage={() => {
                                                                setEnableZoomPicker(false);
                                                            }}
                                                            currentZoomLevel={
                                                                currentZoomLevel
                                                            }
                                                            onItemPressed={
                                                                onZoomChanged
                                                            }
                                                        />
                                                    );
                                                }}
                                            />
                                        </div>
                                    );
                                }}
                            />;
                        }}
                    />
            }
        </Container>
    );
});

interface ContainerProps {
    isAirQualityPage?: boolean;
    isBot?: boolean;
}

/**
 * TODO:
 * Seems we can make this container style reusable
 */

const Container = styled.div<ContainerProps>`
    display: flex;
    flex-direction: row;
    width: 72%;
    margin-left: 10px;
    padding-right: 0;
    top: 0px;
    left: 0px;
    right: 0px;
    bottom: 0px;

    @media ${mediumMediaQuery} {
        width: 52%;
    }

    @media ${smallMediaQuery} {
        margin: ${({ isBot }) => isBot ? '10px 0 10px 0' :  '10px 0 0 0'};
        height: 400px;
        width: ${({ isAirQualityPage }) => isAirQualityPage ? '100%' : '97%'};
    }

    #mapPanZoomContainer {
        bottom: 30px;
        right: 8px;
    }

    #mapZoomPickerControlContainer {
        right: 63px;
        bottom: 85px;
    }

    #layerAttribution {
        bottom: 1px;

        @media ${mediumMediaQuery} {
            bottom: 3px;
            right: 90px;
        }
    }
`;
